import React, { memo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import AOS from "aos/dist/aos";
import withStyles from "@mui/styles/withStyles";
import NavBar from "./navigation/NavBar";
import Footer from "./footer/Footer";
import "aos/dist/aos.css";
import CookieRulesDialog from "./cookies/CookieRulesDialog";
import CookieConsent from "./cookies/CookieConsent";
import dummyBlogPosts from "../content_data/blogPosts";
import dummyVideoPosts from "../content_data/videoPosts";
import DialogSelector from "./register_login/DialogSelector";
import Routing from "./Routing";
import smoothScrollTop from "../../shared/functions/smoothScrollTop";
import axios from "axios";
import { set } from "date-fns";
import jwtDecode from "jwt-decode";
import { useTheme } from "@mui/styles";

AOS.init({ once: true });

const styles = (theme) => ({
  wrapper: {
    backgroundColor: theme.palette.mode === "light" ? "#FFF" : "#000",
    overflowX: "hidden",
  },
});

function Main(props) {
  const { classes, toggleDarkMode, themeMode } = props;
  const [selectedTab, setSelectedTab] = useState(null);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [videoPosts, setVideoPosts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(null);
  const [isCookieRulesDialogOpen, setIsCookieRulesDialogOpen] = useState(false);

  const theme = useTheme();

  useEffect(() => {
    AOS.init();
  }, [theme]);

  const selectHome = useCallback(() => {
    smoothScrollTop();
    document.title = "Auralyze.ai";
    setSelectedTab("Home");
  }, [setSelectedTab]);

  const selectBlog = useCallback(() => {
    smoothScrollTop();
    document.title = "Auralyze.ai- Blog";
    setSelectedTab("Blog");
  }, [setSelectedTab]);

  const selectVideo = useCallback(() => {
    smoothScrollTop();
    document.title = "Auralyze.ai- Videos";
    setSelectedTab("Videos");
  }, [setSelectedTab]);

  const selectPricing = useCallback(() => {
    smoothScrollTop();
    document.title = "Auralyze.ai- Pricing";
    setSelectedTab("Pricing");
  }, [setSelectedTab]);

  const selectFaq = useCallback(() => {
    smoothScrollTop();
    document.title = "Auralyze.ai- Faqs";
    setSelectedTab("FAQs");
  }, [setSelectedTab]);

  const selectCommunity = useCallback(() => {
    smoothScrollTop();
    document.title = "Auralyze.ai- Community";
    setSelectedTab("Community");
  }, [setSelectedTab]);

  const openLoginDialog = useCallback(() => {
    setDialogOpen("login");
    setIsMobileDrawerOpen(false);
  }, [setDialogOpen, setIsMobileDrawerOpen]);

  const closeDialog = useCallback(() => {
    setDialogOpen(null);
  }, [setDialogOpen]);

  const openRegisterDialog = useCallback(() => {
    setDialogOpen("register");
    setIsMobileDrawerOpen(false);
  }, [setDialogOpen, setIsMobileDrawerOpen]);

  const openTermsDialog = useCallback(() => {
    setDialogOpen("termsOfService");
  }, [setDialogOpen]);

  const handleMobileDrawerOpen = useCallback(() => {
    setIsMobileDrawerOpen(true);
  }, [setIsMobileDrawerOpen]);

  const handleMobileDrawerClose = useCallback(() => {
    setIsMobileDrawerOpen(false);
  }, [setIsMobileDrawerOpen]);

  const openChangePasswordDialog = useCallback(() => {
    setDialogOpen("changePassword");
  }, [setDialogOpen]);

  const fetchBlogPosts = useCallback(() => {
    const blogPosts = dummyBlogPosts
      .map((blogPost) => {
        let title = blogPost.title;
        title = title.toLowerCase();
        /* Remove unwanted characters, only accept alphanumeric and space */
        title = title.replace(/[^A-Za-z0-9 ]/g, "");
        /* Replace multi spaces with a single space */
        title = title.replace(/\s{2,}/g, " ");
        /* Replace space with a '-' symbol */
        title = title.replace(/\s/g, "-");
        blogPost.url = `/blog/post/${title}`;
        blogPost.params = `?id=${blogPost.id}`;
        return blogPost;
      })
      .sort((a, b) => b.date - a.date);

    setBlogPosts(blogPosts);
  }, [setBlogPosts]);

  const fetchVideoPosts = useCallback(() => {
    const videoPosts = dummyVideoPosts
      .map((videoPost) => {
        let title = videoPost.title;
        title = title.toLowerCase();
        /* Remove unwanted characters, only accept alphanumeric and space */
        title = title.replace(/[^A-Za-z0-9 ]/g, "");
        /* Replace multi spaces with a single space */
        title = title.replace(/\s{2,}/g, " ");
        /* Replace space with a '-' symbol */
        title = title.replace(/\s/g, "-");
        videoPost.url = `/video/post/${title}`;
        videoPost.params = `?id=${videoPost.id}`;
        return videoPost;
      })
      .sort((a, b) => b.date - a.date);
    setVideoPosts(videoPosts);
  }, [setVideoPosts]);

  const handleCookieRulesDialogOpen = useCallback(() => {
    setIsCookieRulesDialogOpen(true);
  }, [setIsCookieRulesDialogOpen]);

  const handleCookieRulesDialogClose = useCallback(() => {
    setIsCookieRulesDialogOpen(false);
  }, [setIsCookieRulesDialogOpen]);

  useEffect(fetchBlogPosts, [fetchBlogPosts]);
  useEffect(fetchVideoPosts, [fetchVideoPosts]);

  const [userLoggedIn, setUserLoggedIn] = useState({
    logged: false,
    hasResume: false,
  });
  useEffect(() => {
    const checkResume = async () => {
      if (localStorage.getItem("jwtToken")) {
        const token = localStorage.getItem("jwtToken");
        const decoded = await jwtDecode(token);
        setUserLoggedIn({ logged: true, name: decoded.name });
        const res = await axios.post(`/api/users/user-resume`);

        if (res.data.resume) {
          setUserLoggedIn({
            logged: true,
            hasResume: true,
            name: decoded.name,
          });
        }
      }
    };
    checkResume();
  }, []);

  const [openCreatePost, setOpenCreatePost] = useState(false);

  const handleToggleCreatePost = () => {
    setOpenCreatePost(!openCreatePost);
  };

  return (
    <div className={classes.wrapper}>
      {!isCookieRulesDialogOpen && (
        <CookieConsent
          handleCookieRulesDialogOpen={handleCookieRulesDialogOpen}
        />
      )}
      <DialogSelector
        openLoginDialog={openLoginDialog}
        dialogOpen={dialogOpen}
        onClose={closeDialog}
        openTermsDialog={openTermsDialog}
        openRegisterDialog={openRegisterDialog}
        openChangePasswordDialog={openChangePasswordDialog}
      />
      <CookieRulesDialog
        open={isCookieRulesDialogOpen}
        onClose={handleCookieRulesDialogClose}
      />
      <NavBar
        selectedTab={selectedTab}
        selectTab={setSelectedTab}
        openLoginDialog={openLoginDialog}
        mobileDrawerOpen={isMobileDrawerOpen}
        handleMobileDrawerOpen={handleMobileDrawerOpen}
        handleMobileDrawerClose={handleMobileDrawerClose}
        userLoggedIn={userLoggedIn}
        toggleDarkMode={toggleDarkMode}
        themeMode={themeMode}
      />
      <Routing
        blogPosts={blogPosts}
        videoPosts={videoPosts}
        selectHome={selectHome}
        selectBlog={selectBlog}
        selectVideo={selectVideo}
        selectFaq={selectFaq}
        selectPricing={selectPricing}
        userLoggedIn={userLoggedIn}
        openLoginDialog={openLoginDialog}
        selectCommunity={selectCommunity}
        openCreatePost={openCreatePost}
        handleToggleCreatePost={handleToggleCreatePost}
        themeMode={themeMode}
      />
      <Footer />
    </div>
  );
}

Main.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles, { withTheme: true })(memo(Main));
